import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FaArrowLeft } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';

import { OrderStatus } from '@/components/chat/utils/order-status';
import LoadingSkeletonModalTable from '@/components/modal/prompt/LoadingSkeletonModalTable';
import { formatDateTime, formatPrice } from '@/components/utils/utils';
import { useUser } from '@/hooks/useUser';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { IOrder } from '@/interfaces/api/orders/IOrder';
import { UserRole } from '@/interfaces/api/user/UserRole.enum';
import { notificationService } from '@/services/notification.service';
import { ordersApiService } from '@/services/order.service';

const OrderViewPage = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { user } = useUser();
	const userIsConsumer = user?.roles.includes(UserRole.Consumer);

	// Fetch order details
	const {
		data: order,
		isLoading: orderLoading,
		isError: orderError,
		error: orderErrorData,
	} = useQuery<IOrder>({
		queryKey: [ApiQueryKeys.getOrderById, id],
		queryFn: () => ordersApiService.getOrderById(id!),
		enabled: !!id,
	});

	// Mutation for updating order status
	const { mutate: updateStatus } = useMutation({
		mutationFn: (status: OrderStatus) =>
			ordersApiService.updateOrderStatus(id ?? '', status),
		onSuccess: (updatedOrder) => {
			queryClient.setQueryData([ApiQueryKeys.getOrderById, id], updatedOrder);
			notificationService.success('Order status updated successfully');
		},
		onError: (error: Error) => {
			notificationService.error(
				error.message || 'Failed to update order status',
			);
		},
	});

	if (orderError) {
		if (orderErrorData instanceof Error) {
			notificationService.error(orderErrorData.message);
		} else {
			notificationService.error('Unknown error fetching order details');
		}
		navigate(-1);
	}

	const handleStatusChange = (newStatus: OrderStatus) => {
		updateStatus(newStatus);
	};

	const statusOptions = Object.values(OrderStatus).filter(
		(status) => status !== order?.status,
	);

	const orderInfo = [
		{ label: 'Order ID', value: order?.id },
		{ label: 'Customer ID', value: order?.customerId },
		{
			label: 'Date Created',
			value: order ? formatDateTime(order?.createdAt) : '',
		},
		{
			label: 'Last Updated',
			value: order ? formatDateTime(order?.updatedAt) : '',
		},
	];

	const productInfo = [
		{ label: 'Title', value: order?.orderDetails?.product?.title },
		{ label: 'Tag', value: order?.orderDetails?.product?.tags },
		{ label: 'Handle', value: order?.orderDetails?.product?.handle },
		{ label: 'Id', value: order?.orderDetails?.product?.id },
	];
	const billInfo = {
		originalPrice: order?.orderDetails?.pricing?.originalPrice,
		finalPrice: order?.orderDetails?.pricing?.finalPrice,
		units: order?.orderDetails?.pricing?.quantity,
		total: order?.orderDetails?.pricing?.total ?? '',
	};

	return (
		<div className=" min-h-content p-4 md:p-6">
			{orderLoading && <LoadingSkeletonModalTable />}

			{order && !orderLoading && (
				<div className="max-w-8xl mx-auto">
					{/* Header with back button */}
					<div className="flex justify-between items-center mb-4 sm:mb-6">
						<button
							onClick={() => navigate(-1)}
							className="flex items-center text-blue-600 hover:text-blue-800 gap-2 sm:gap-3 flex-shrink-0"
						>
							<span className="text-sm sm:text-base">
								<FaArrowLeft />
							</span>
							<p className="hidden sm:block sm:text-base text-sm">
								Back to Orders
							</p>
						</button>
						<h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-grey-90 text-center px-2 sm:px-4">
							Order Details
						</h1>
						<div className="w-16 sm:w-20 lg:w-24 flex-shrink-0"></div>{' '}
						{/* Responsive spacer for alignment */}
					</div>

					{/* Order Summary Card */}
					<div className="bg-grey-0 rounded-large shadow p-6 mb-6">
						<div className="flex flex-col md:flex-row justify-between gap-6 mb-6">
							{/* Order Info */}
							<div className="space-y-4 flex-1">
								<div>
									<h2 className="text-xl font-semibold mb-4 text-grey-80">
										Order Information
									</h2>
									<div className="grid grid-cols-2 gap-4">
										{orderInfo.map((item, index) => (
											<div key={index}>
												<p className="text-sm text-grey-50">{item.label}</p>
												<p className="font-medium text-grey-80  overflow-hidden text-ellipsis whitespace-nowrap">
													{item.value}
												</p>
											</div>
										))}
									</div>
								</div>

								{/* Status Management */}
								<div>
									<div className="flex items-center gap-4">
										<p className="text-sm text-grey-50">Current Status</p>
										<span
											className={`px-3 py-1 text-sm rounded-large ${
												order.status === OrderStatus.PENDING
													? 'bg-yellow-100 text-yellow-800'
													: order.status === OrderStatus.PROCESSING
													? 'bg-blueBrand-100 text-blueBrand-800'
													: order.status === OrderStatus.SHIPPED
													? 'bg-purpleBrand-100 text-purpleBrand-800'
													: order.status === OrderStatus.DELIVERED
													? 'bg-purpleBrand-200 text-purpleBrand-700'
													: 'bg-redBrand-100 text-redBrand-800'
											}`}
										>
											{order.status}
										</span>
									</div>

									{!userIsConsumer &&
										user?.username === order.vendorEmail &&
										statusOptions.length > 0 && (
											<div className="mt-3">
												<label className="block text-sm font-medium text-grey-50 mb-1">
													Update Status
												</label>
												<div className="flex flex-wrap gap-2">
													{statusOptions.map((status) => (
														<button
															key={status}
															onClick={() => handleStatusChange(status)}
															className={`px-3 py-1 text-sm rounded-large transition-colors ${
																status === OrderStatus.PROCESSING
																	? 'bg-blueBrand-50 text-blueBrand-600 hover:bg-blueBrand-100'
																	: status === OrderStatus.SHIPPED
																	? 'bg-purpleBrand-50 text-purpleBrand-500 hover:bg-purpleBrand-100'
																	: status === OrderStatus.DELIVERED
																	? 'bg-purpleBrand-100 text-purpleBrand-700 hover:bg-purpleBrand-200'
																	: 'bg-grey-10 text-grey-70 hover:bg-grey-20'
															}`}
														>
															{status}
														</button>
													))}
												</div>
											</div>
										)}
								</div>
							</div>

							{billInfo && (
								<div className="flex flex-col lg:min-w-[20rem]  lg:mr-[5rem] ">
									<h2 className="text-xl font-semibold mb-2 text-grey-80">
										Receipt
									</h2>
									<div className="flex flex-col gap-2 mt-2">
										<div className="flex items-center gap-2 w-full justify-between">
											<span className="text-sm text-gray-700">
												Original Price
											</span>
											<span className="text-sm font-medium line-through text-gray-500">
												{formatPrice(billInfo.originalPrice?.toString() ?? '0')}
											</span>
										</div>
										<div className="flex items-center gap-2 w-full justify-between">
											<span className="text-sm text-gray-700">
												Discounted Price
											</span>
											<span className="text-sm font-medium text-gray-900">
												{formatPrice(billInfo.finalPrice?.toString() ?? '0')}
											</span>
										</div>
										<div className="flex items-center gap-2 w-full justify-between">
											<span className="text-sm text-gray-700">Units</span>
											<span className="text-sm font-medium text-gray-900">
												{billInfo?.units}
											</span>
										</div>
										<div className="flex items-center gap-2 justify-between border-t-2 py-2 mt-2">
											<span className="text-sm text-gray-700">
												Total (GST included)
											</span>
											<span className="text-sm font-medium text-gray-900">
												{formatPrice(billInfo.total?.toString() ?? '0')}
											</span>
										</div>
									</div>
								</div>
							)}
						</div>
					</div>

					{/* Address Information */}
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
						{!orderLoading && order?.orderDetails && (
							<div className="bg-grey-0 rounded-large shadow p-6">
								<div className="space-y-4">
									<h2 className="text-xl font-semibold text-grey-80">
										Product Summary
									</h2>

									<div className="grid grid-cols-2 gap-4">
										<div className="flex justify-center h-[200px] w-full">
											<img
												alt={order?.orderDetails?.product?.title ?? ''}
												src={order?.orderDetails?.product?.imageSrc ?? ''}
												loading="lazy"
												className="h-full w-full object-contain rounded-lg"
											/>
										</div>
										<div className="grid grid-cols-2 gap-4">
											{productInfo.map((item, index) => (
												<div key={index}>
													<p className="text-sm text-grey-50">{item.label}</p>
													<p
														className={`font-medium ${
															item.label === 'Id'
																? 'text-lg text-grey-90'
																: 'text-grey-80'
														} overflow-hidden text-ellipsis whitespace-nowrap`}
													>
														{item.value}
													</p>
												</div>
											))}
										</div>
									</div>
								</div>
							</div>
						)}
						<div className="bg-grey-0 rounded-large shadow p-6 ">
							<h2 className="text-xl font-semibold mb-4 text-grey-80">
								Customer Information
							</h2>
							<div className="grid grid-cols-2 gap-4">
								<div>
									<p className="text-sm text-grey-50">Name</p>
									<p className="font-medium text-grey-80">
										{order.customerName}
									</p>
								</div>
								<div>
									<p className="text-sm text-grey-50">Email</p>
									<p className="font-medium text-grey-80  overflow-hidden text-ellipsis whitespace-nowrap">
										{order.customerEmail}
									</p>
								</div>
							</div>
							<h2 className="text-xl font-semibold mb-4 text-grey-80 mt-5">
								Shipping Address
							</h2>
							<p className="whitespace-pre-line text-grey-70">
								{order.shippingAddress}
							</p>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default OrderViewPage;
